import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import { medicalTeam } from '../data/mock';
import {
  ChevronRight, Stethoscope, Apple, Heart, ArrowRight, Quote, GraduationCap, CheckCircle,
} from 'lucide-react';

const ICONS = { Stethoscope, Apple, Heart };

// Stable slugs mapped to the existing medical team (ids 1-3, non-network).
const SLUG_TO_ID = {
  'dr-pooja-sree': 1,
  'k-pavani': 2,
  'dr-mrudula': 3,
};

const SITE = 'https://tcubefitness.com';

const AuthorProfilePage = () => {
  const { slug } = useParams();
  const id = SLUG_TO_ID[slug];
  const member = medicalTeam.find((m) => m.id === id);

  useEffect(() => {
    if (!member) return;
    const scriptId = 'tcube-author-schema';
    const data = {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      '@id': `${SITE}/author/${slug}#profile`,
      mainEntity: {
        '@type': 'Person',
        name: member.name,
        jobTitle: member.role,
        description: member.description,
        hasCredential: member.qualifications,
        knowsAbout: member.expertise,
        worksFor: { '@type': 'Organization', name: 'TCUBE FITNESS', url: SITE },
        url: `${SITE}/author/${slug}`,
      },
    };
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { const e = document.getElementById(scriptId); if (e) e.remove(); };
  }, [member, slug]);

  if (!member) return <Navigate to="/our-team" replace />;

  const Icon = ICONS[member.icon] || Stethoscope;
  const url = `/author/${slug}`;

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>{member.name} — {member.role} | TCUBE FITNESS™</title>
        <meta name="description" content={`${member.name}, ${member.role} at TCUBE FITNESS (${member.qualifications}). ${member.specialty}.`} />
        <link rel="canonical" href={`${SITE}${url}`} />
      </Helmet>

      <SEOSchema
        type="webpage"
        name={`${member.name} — ${member.role}`}
        description={member.description}
        url={url}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Our Team', path: '/our-team' },
          { name: member.name, path: url },
        ]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16" data-testid={`author-page-${slug}`}>
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">

          <nav aria-label="Breadcrumb" className="mb-6 text-xs sm:text-sm">
            <ol className="flex items-center gap-1.5 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li><Link to="/our-team" className="hover:text-white">Our Team</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li className="text-slate-300">{member.name}</li>
            </ol>
          </nav>

          {/* Hero */}
          <section className="flex flex-col sm:flex-row items-start gap-6 mb-12">
            <div className="w-20 h-20 rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 flex-shrink-0">
              <Icon className="w-10 h-10" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{member.name}</h1>
              <p className="text-blue-300 font-semibold mb-3">{member.role}</p>
              <p className="inline-flex items-center gap-1.5 text-amber-200 text-xs sm:text-sm font-semibold bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1">
                <GraduationCap className="w-4 h-4" aria-hidden="true" /> {member.qualifications}
              </p>
            </div>
          </section>

          {/* About */}
          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">About {member.name}</h2>
            <p className="text-slate-300 leading-relaxed">{member.description}</p>
          </section>

          {/* Philosophy */}
          {member.philosophy && (
            <section className="mb-10">
              <blockquote className="bg-slate-900/60 border-l-4 border-blue-500 rounded-r-2xl p-6 flex gap-3">
                <Quote className="w-6 h-6 text-blue-400 flex-shrink-0" aria-hidden="true" />
                <p className="text-slate-200 italic leading-relaxed">{member.philosophy}</p>
              </blockquote>
            </section>
          )}

          {/* Expertise */}
          {member.expertise?.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Areas of focus</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {member.expertise.map((e, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-300 text-sm sm:text-base">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Editorial note */}
          <section className="mb-12">
            <p className="text-slate-400 text-sm leading-relaxed">
              {member.name} contributes to and reviews TCUBE FITNESS content in line with our{' '}
              <Link to="/editorial-policy" className="text-emerald-400 hover:underline underline-offset-2">Editorial &amp; Medical Review Policy</Link>.
              This profile is for transparency and does not constitute individual medical advice.
            </p>
          </section>

          {/* CTA */}
          <section className="flex flex-col sm:flex-row gap-3">
            <Link to="/book-consultation?from=author-profile" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-6 py-3 rounded-full font-semibold text-sm sm:text-base" data-testid="author-cta-book">
              Book a Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/our-team" className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-full font-semibold text-sm" data-testid="author-team-link">
              Meet the full team
            </Link>
          </section>

        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default AuthorProfilePage;
